import { Injectable } from '@angular/core';
import { Action, Selector, State, StateToken, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { Undoable } from '@medsurf/flat-decorators';
import { HistoryEntityBaseState } from '../base/history-entity.base.state';

export const FREE_FORM_STYLE_ENTITY_TOKEN = new StateToken<FreeFormStyleEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.FREE_FORM_STYLE_ENTITY);

export type FreeFormStyleEntityStateModel = FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<FlatModels.FreeFormStyleEntityModels.FreeFormStyle>;

@State<FreeFormStyleEntityStateModel>({
  name: FREE_FORM_STYLE_ENTITY_TOKEN,
  defaults: {
    snapshots: {},
    entities: {},
  }
})
@Injectable()
export class FreeFormStyleEntityState extends HistoryEntityBaseState<FlatModels.FreeFormStyleEntityModels.FreeFormStyle, FreeFormStyleEntityStateModel> {
  //<editor-fold desc="Selectors">

  @Selector([FreeFormStyleEntityState])
  static override typedEntities$(state: FreeFormStyleEntityStateModel): { [id: string]: FlatModels.FreeFormStyleEntityModels.FreeFormStyle } {
    return HistoryEntityBaseState.typedEntities$(state);
  }

  @Selector([FreeFormStyleEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.FreeFormStyleEntityModels.FreeFormStyle }): FlatModels.FreeFormStyleEntityModels.FreeFormStyle[] {
    return HistoryEntityBaseState.entities$(typedEntities);
  }

  @Selector([FreeFormStyleEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.FreeFormStyleEntityModels.FreeFormStyle }) {
    return HistoryEntityBaseState.entity$(typedEntities);
  }

  @Selector([FreeFormStyleEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.FreeFormStyleEntityModels.FreeFormStyle }) {
    return HistoryEntityBaseState.entitiesByIds$(typedEntities);
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">

  @Action(FlatActions.FreeFormStyleEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<FreeFormStyleEntityStateModel>,
                            action: FlatActions.FreeFormStyleEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }

  @Action(FlatActions.FreeFormStyleEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<FreeFormStyleEntityStateModel>,
                              action: FlatActions.FreeFormStyleEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }

  @Action(FlatActions.FreeFormStyleEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<FreeFormStyleEntityStateModel>,
                               action: FlatActions.FreeFormStyleEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }

  @Action(FlatActions.FreeFormStyleEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<FreeFormStyleEntityStateModel>,
                                 action: FlatActions.FreeFormStyleEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">

  @Action(FlatActions.FreeFormStyleEntityActions.AddEntity)
  @Undoable(
    FlatActions.FreeFormStyleEntityActions.AddEntity,
    FREE_FORM_STYLE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD
  )
  override addEntity(state: StateContext<FreeFormStyleEntityStateModel>,
                     action: FlatActions.FreeFormStyleEntityActions.AddEntity) {
    return super.addEntity(state, action);
  }

  @Action(FlatActions.FreeFormStyleEntityActions.UpdateEntity)
  @Undoable(
    FlatActions.FreeFormStyleEntityActions.UpdateEntity,
    FREE_FORM_STYLE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE
  )
  override updateEntity(state: StateContext<FreeFormStyleEntityStateModel>,
                        action: FlatActions.FreeFormStyleEntityActions.UpdateEntity) {
    return super.updateEntity(state, action);
  }

  @Action(FlatActions.FreeFormStyleEntityActions.RemoveEntity)
  @Undoable(
    FlatActions.FreeFormStyleEntityActions.RemoveEntity,
    FREE_FORM_STYLE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE
  )
  override removeEntity(state: StateContext<FreeFormStyleEntityStateModel>,
                        action: FlatActions.FreeFormStyleEntityActions.RemoveEntity) {
    return super.removeEntity(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Snapshot">

  @Action(FlatActions.FreeFormStyleEntityActions.UndoSnapshot)
  override undoSnapshot(state: StateContext<FreeFormStyleEntityStateModel>,
                        action: FlatActions.FreeFormStyleEntityActions.UndoSnapshot) {
    return super.undoSnapshot(state, action);
  }

  @Action(FlatActions.FreeFormStyleEntityActions.RedoSnapshot)
  override redoSnapshot(state: StateContext<FreeFormStyleEntityStateModel>,
                        action: FlatActions.FreeFormStyleEntityActions.RedoSnapshot) {
    return super.redoSnapshot(state, action);
  }

  @Action(FlatActions.FreeFormStyleEntityActions.CleanUpSnapshots)
  override cleanUpSnapshots(state: StateContext<FreeFormStyleEntityStateModel>,
                            action: FlatActions.FreeFormStyleEntityActions.CleanUpSnapshots) {
    return super.cleanUpSnapshots(state, action);
  }

  //</editor-fold>

  //</editor-fold>
}
